import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AnimatedComponentWrapper, { useAnimationPreviewReplay } from './AnimatedComponentWrapper';
import { ANIMATION_PRESETS } from '../utils/componentAnimation';

function PreviewSample({ componentType }) {
  if (componentType === 'button') {
    return (
      <div className="px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium shadow-sm">
        Bouton exemple
      </div>
    );
  }
  if (componentType === 'badge') {
    return (
      <span className="inline-flex px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-semibold">
        Nouveau
      </span>
    );
  }
  if (componentType === 'image') {
    return (
      <div className="w-full max-w-[220px] aspect-[4/3] rounded-xl bg-gradient-to-br from-sky-200 to-indigo-300 shadow-inner flex items-center justify-center">
        <Icon name="Image" size={32} className="text-white/70" />
      </div>
    );
  }
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm max-w-[260px]">
      <p className="text-sm font-semibold text-foreground">Aperçu animation</p>
      <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
        Le rendu final dépend du contenu de votre composant.
      </p>
    </div>
  );
}

export default function ComponentAnimationPreview({ animation, componentType, onReplay, tall = false }) {
  const { previewKey, replay } = useAnimationPreviewReplay(animation, true);
  const preset = ANIMATION_PRESETS.find((p) => p.id === animation?.preset);

  const handleReplay = () => {
    replay();
    onReplay?.();
  };

  return (
    <div className="rounded-xl border border-border bg-muted/20 overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-card/80">
        <div className="flex items-center gap-2 text-xs font-medium text-foreground">
          <Icon name="Play" size={14} className="text-primary" />
          Aperçu
          {animation?.enabled && preset ? (
            <span className="text-muted-foreground font-normal">· {preset.label}</span>
          ) : null}
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          onClick={handleReplay}
          disabled={!animation?.enabled}
        >
          <Icon name="RotateCcw" size={12} className="mr-1.5" />
          Rejouer
        </Button>
      </div>

      <div
        className={`relative flex items-center justify-center overflow-hidden ${
          tall ? 'h-64 lg:h-[min(62vh,560px)]' : 'h-44'
        }`}
        style={{
          backgroundImage:
            'linear-gradient(to right, hsl(var(--border) / 0.35) 1px, transparent 1px), linear-gradient(to bottom, hsl(var(--border) / 0.35) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
        }}
      >
        {!animation?.enabled ? (
          <p className="text-xs text-muted-foreground px-4 text-center">
            Activez une animation pour voir l’aperçu ici.
          </p>
        ) : (
          <AnimatedComponentWrapper
            comp={{ props: { animation } }}
            previewMode
            previewKey={previewKey}
            forceActive={false}
          >
            <PreviewSample componentType={componentType} />
          </AnimatedComponentWrapper>
        )}
      </div>
    </div>
  );
}
